import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { errorMessage, httpStatus, platformApi } from './client';
import { AuditView, OperationsView, OverviewView, SalesView } from './DashboardViews';
import { PoliciesView } from './PoliciesView';
import { UsersView } from './UsersView';
import { DepositsView } from './DepositsView';
import { SellersView } from './SellersView';
import { SettlementsView } from './SettlementsView';
import { PgSettingsView } from './PgSettingsView';
import { Icon } from './shared';
import { Session } from './types';
import './PlatformAdmin.css';
import './Overview.css';
import { AnalyticsView } from '../Analytics/AnalyticsView';
import '../Analytics/PlatformAnalytics.css';
import { useLocation } from 'react-router-dom';

const navigation = [
  {key: 'overview', title: '운영 대시보드', description: '팬버드 전체 서비스의 주요 지표와 운영 현황입니다.'},
  {key: 'analytics', title: '데이터 분석', description: '유입·마케팅·실제 구매와 재구매를 분석합니다.'},
  {key: 'deposits', title: '코인 무통장 입금', description: '셀러의 코인 충전 요청과 실제 입금 내역을 확인하고 처리합니다.'},
  {key: 'sellers', title: '셀러 관리', description: '입점 신청을 검토하고 셀러 운영 정지·해제를 관리합니다.'},
  {key: 'pg-settings', title: 'PG 설정', description: '본사 PG 계약을 관리합니다.'},
  {key: 'users', title: '전체 유저 관리', description: '일반 회원과 셀러의 정보 및 이용 상태를 관리합니다.'},
  {key: 'sales', title: '셀러별 매출', description: '스토어별 결제액, 환불액, 순매출을 기간별로 확인합니다.'},
  {key: 'settlements', title: '업체별 정산', description: '정산 기간별 업체 지급 예정액과 지급 상태를 확인합니다.'},
  {key: 'policies', title: '약관 · 개인정보처리방침', description: '일반 회원과 셀러의 공개 정책을 작성하고 버전별로 관리합니다.'},
  {key: 'operations', title: '운영 현황', description: '결제, 환불, 문의와 주문에서 확인이 필요한 항목을 모았습니다.'},
  {key: 'audit', title: '관리자 활동 기록', description: '관리자 작업과 변경 사유를 확인합니다.'},
];
type AuthState = 'loading' | 'ready' | 'login' | 'forbidden' | 'error';

export default function PlatformAdmin() {
  const [auth, setAuth] = useState<AuthState>('loading');
  const [session, setSession] = useState<Session | null>(null);
  const [authError, setAuthError] = useState('');
  const [retry, setRetry] = useState(0);
  const [logoutBusy, setLogoutBusy] = useState(false);
  const [logoutError, setLogoutError] = useState('');
  const [dirty, setDirty] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const logoutLock = useRef(false);
  const [params] = useSearchParams();
  const routeNavigate = useNavigate();
  const route = useLocation();
  const requested = route.pathname.replace(/\/+$/, '').endsWith('/analytics') ? 'analytics' : params.get('view') || 'overview';
  const current = navigation.find(item => item.key === requested) || navigation[0];
  const onAuth = useCallback((status: number) => {
    setSession(null); setDirty(false); setAuth(status === 403 ? 'forbidden' : 'login');
    setAuthError(status === 403 ? '팬버드 운영 관리자 권한이 필요합니다.' : '로그인이 만료되었습니다. 다시 로그인해 주세요.');
  }, []);
  useEffect(() => {
    const controller = new AbortController();
    setAuth('loading'); setAuthError('');
    platformApi.get<Session>('/session', {signal: controller.signal}).then(response => {
      if (controller.signal.aborted) return;
      setSession(response.data); setAuth('ready');
    }).catch(error => {
      if (controller.signal.aborted) return;
      const status = httpStatus(error);
      setAuth(status === 401 ? 'login' : status === 403 ? 'forbidden' : 'error');
      if (status !== 401) setAuthError(errorMessage(error));
    });
    return () => controller.abort();
  }, [retry]);
  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${current.title} | 팬버드 관리자`;
    return () => {document.title = previousTitle;};
  }, [current.title]);
  useEffect(() => {
    if (!dirty) return;
    const beforeUnload = (event: BeforeUnloadEvent) => {event.preventDefault(); event.returnValue = '';};
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [dirty]);
  const allowLeave = () => !dirty || window.confirm('저장하지 않은 변경 내용 또는 처리 중인 요청이 있습니다. 이 화면을 떠날까요?');
  const navigate = (key: string) => {if (key !== current.key && allowLeave()) {setDirty(false); routeNavigate(key === 'analytics' ? '/admin_main/analytics' : key === 'overview' ? '/admin_main' : `/admin_main?view=${key}`);}};
  const reload = () => {if (allowLeave()) {setDirty(false); setRefresh(value => value + 1);}};
  const logout = async () => {
    if (logoutLock.current || !allowLeave()) return;
    logoutLock.current = true; setLogoutBusy(true); setLogoutError('');
    try {
      await platformApi.post('/logout');
      setSession(null); setAuth('login'); setAuthError(''); setDirty(false);
    } catch (error) {
      if (httpStatus(error) === 401) {setSession(null); setAuth('login'); setDirty(false);}
      else setLogoutError(errorMessage(error));
    } finally {logoutLock.current = false; setLogoutBusy(false);}
  };
  if (auth !== 'ready' || !session) return <main className="pa-root pa-auth"><div className="pa-auth-brand"><Brand /><span>PLATFORM ADMINISTRATION</span></div><div className="pa-auth-card">
    {auth === 'loading' ? <div className="pa-state" role="status"><span className="pa-spinner" />관리자 세션을 확인하고 있습니다.</div> : auth === 'error' ? <><span className="pa-eyebrow">CONNECTION</span><h1>연결을 확인해 주세요</h1><p role="alert">{authError}</p><button type="button" className="pa-button pa-button-primary" onClick={() => setRetry(value => value + 1)}>다시 시도</button></> : auth === 'forbidden' ? <><span className="pa-eyebrow">ACCESS RESTRICTED</span><h1>운영 관리자 권한이 필요합니다</h1><p role="alert">{authError || '이 계정에는 팬버드 플랫폼 관리 권한이 없습니다.'}</p><div className="pa-note"><p>사전에 운영 관리자로 등록된 계정만 접근할 수 있습니다. 계정 권한은 팬버드 운영 담당자에게 확인해 주세요.</p></div><button type="button" className="pa-button pa-button-primary" onClick={() => {setAuth('login'); setAuthError('');}}>다른 계정으로 로그인</button></> : <Login initialError={authError} onSuccess={value => {setSession(value); setAuth('ready'); setAuthError('');}} onForbidden={message => {setAuth('forbidden'); setAuthError(message);}} />}
    </div><p className="pa-auth-foot">팬버드 운영 관리자 전용 · 접근 및 변경 내역이 기록됩니다.</p><a className="pa-back-link" href="/">팬버드 서비스로 돌아가기 ↗</a></main>;
  return <div className={`pa-root pa-shell${current.key === 'analytics' ? ' pa-analytics' : ''}`}><aside className="pa-sidebar"><div className="pa-sidebar-brand"><Brand /><span>플랫폼 관리자</span></div><div className="pa-nav-label">WORKSPACE</div><nav aria-label="플랫폼 관리 메뉴">{navigation.map(item => <button type="button" key={item.key} className={`pa-nav-item${current.key === item.key ? ' is-active' : ''}`} onClick={() => navigate(item.key)} aria-current={current.key === item.key ? 'page' : undefined}><Icon name={item.key} /><span>{item.title}</span></button>)}</nav><div className="pa-sidebar-bottom"><a href="/" target="_blank" rel="noreferrer">팬버드 서비스 열기 <span>↗</span></a><div className="pa-operator"><span className="pa-avatar">{(session.name || session.nickname || session.userid).slice(0, 1)}</span><div><strong>{session.name || session.nickname || session.userid}</strong><small>운영 관리자</small></div><button type="button" onClick={logout} disabled={logoutBusy} aria-label="관리자 로그아웃" title="로그아웃">{logoutBusy ? '…' : '↪'}</button></div>{logoutError && <p className="pa-inline-error" role="alert">{logoutError}</p>}</div></aside><div className="pa-workspace"><main className={`pa-main${current.key === 'overview' ? ' pa-dashboard' : ''}`}><div className="pa-page-heading"><div><h1>{current.title}</h1><p>{current.description}</p></div><button type="button" className="pa-button" onClick={reload} aria-label="현재 화면 새로고침">↻ <span>새로고침</span></button></div><div className="pa-content" key={`${current.key}:${refresh}`}>
    {current.key === 'overview' && <OverviewView onAuth={onAuth} navigate={navigate} />}
    {current.key === 'analytics' && <AnalyticsView platform onAuth={onAuth} onDirty={setDirty} />}
    {current.key === 'deposits' && <DepositsView onAuth={onAuth} />}
    {current.key === 'sellers' && <SellersView onAuth={onAuth} />}
    {current.key === 'pg-settings' && <PgSettingsView onAuth={onAuth} />}
    {current.key === 'users' && <UsersView onAuth={onAuth} />}
    {current.key === 'sales' && <SalesView onAuth={onAuth} />}
    {current.key === 'settlements' && <SettlementsView onAuth={onAuth} />}
    {current.key === 'policies' && <PoliciesView onAuth={onAuth} onDirty={setDirty} />}
    {current.key === 'operations' && <OperationsView onAuth={onAuth} />}
    {current.key === 'audit' && <AuditView onAuth={onAuth} />}
  </div><footer className="pa-footer">FANBIRD · PLATFORM ADMIN<span>모든 일시는 한국 시간(KST) 기준입니다.</span></footer></main></div></div>;
}

function Brand() {return <div className="pa-brand"><span aria-hidden="true">f</span><strong>fanbird<span>.</span></strong></div>;}

function Login({initialError, onSuccess, onForbidden}: {initialError: string; onSuccess: (session: Session) => void; onForbidden: (message: string) => void}) {
  const [userid, setUserid] = useState('');
  const [userpw, setUserpw] = useState('');
  const [error, setError] = useState(initialError);
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);
  const alive = useRef(true);
  useEffect(() => {alive.current = true; return () => {alive.current = false;};}, []);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (lock.current || !userid.trim() || !userpw) return;
    lock.current = true; setBusy(true); setError('');
    try {
      const result = await platformApi.post<Session>('/login', {userid: userid.trim(), userpw});
      if (alive.current) {setUserpw(''); onSuccess(result.data);}
    } catch (error) {
      if (!alive.current) return;
      setUserpw('');
      if (httpStatus(error) === 403) onForbidden(errorMessage(error));
      else setError(httpStatus(error) === 401 ? '아이디 또는 비밀번호를 확인해 주세요.' : errorMessage(error));
    } finally {lock.current = false; if (alive.current) setBusy(false);}
  };
  return <><span className="pa-eyebrow">WELCOME TO FANBIRD</span><h1>팬버드 관리자 로그인</h1><p>플랫폼 운영을 위한 관리자 공간입니다.</p><form className="pa-login-form" onSubmit={submit}><label>관리자 아이디<input autoComplete="username" value={userid} onChange={event => setUserid(event.target.value)} placeholder="관리자 아이디를 입력해 주세요" disabled={busy} required /></label><label>비밀번호<input type="password" autoComplete="current-password" value={userpw} onChange={event => setUserpw(event.target.value)} placeholder="비밀번호를 입력해 주세요" disabled={busy} required /></label>{error && <div className="pa-inline-error" role="alert">{error}</div>}<button type="submit" className="pa-button pa-button-primary" disabled={busy || !userid.trim() || !userpw}>{busy ? '로그인 중…' : '관리자 로그인'}</button></form><div className="pa-note"><strong>운영 권한이 등록된 계정만 접근할 수 있습니다.</strong><p>운영 관리자 계정 등록은 팬버드 운영 담당자를 통해 진행해 주세요.</p></div></>;
}
